import { unique } from "remeda";
import { selectorsData, type TSelectorExchange, type TSelectorMaturity } from "../../../market-grid/selectorsData";
import { captureEvent } from "../../../../utils/o11y";
import menuItems, { promotedSelectors } from "../../../market-grid/menuItems";
import { InputPipeIcon } from "../../../icons/InputPipeIcon";
import type { MenuConfig } from "../../types";
import { any, anyColumn, column, selection, recipes } from "../../disabled-helpers";

export const productMaturityOverrides: Record<string, TSelectorMaturity<TSelectorExchange>> = {
  "8df15c9d-d971-43c6-bb18-d808a7af0fd3": "fx",
  "b8973c43-540b-477c-aa48-5dcd68139eba": "fx",
  "6c772c57-3604-4222-8f7e-3f3ebf453eaf": "fx",
  "46a81843-2ef3-470e-a7f8-4b452102b625": "fx",
};

export const fieldNameOverrides: Record<string, string> = {
  fv: "FAIR VALUE",
};

export type TSelectorUsage<
  T extends TSelectorExchange,
  U extends TSelectorMaturity<T>,
> = keyof (typeof selectorsData)[T][U];

export function selectors({
  exchange,
  maturity,
  usage,
}: {
  exchange: TSelectorExchange;
  maturity: TSelectorMaturity<TSelectorExchange>;
  usage: TSelectorUsage<TSelectorExchange, TSelectorMaturity<TSelectorExchange>>;
}) {
  const exchangeData = exchange in selectorsData ? selectorsData[exchange] : null;

  if (!exchangeData) {
    return ["value"];
  }

  const maturityData = maturity in exchangeData ? exchangeData[maturity] : null;

  if (!maturityData) {
    return ["value"];
  }

  const usageData = usage in maturityData ? maturityData[usage] : null;

  return usageData || ["value"];
}

export function getSelectors({
  exchange,
  maturity,
  productId,
}: {
  exchange: TSelectorExchange;
  maturity: TSelectorMaturity<TSelectorExchange>;
  productId: string;
}) {
  const selectorMaturity = productMaturityOverrides?.[productId] || maturity;

  const result = selectors({
    exchange,
    maturity: selectorMaturity,
    usage: "grid",
  });

  return result.map((selector) => {
    const selectorLabel = selector in fieldNameOverrides ? fieldNameOverrides[selector] : selector;

    return {
      label: selectorLabel.toUpperCase(),
      value: selector,
      groupValue: "field",
    };
  });
}

export function selectorSubgroup(): Record<string, string> {
  return menuItems.reduce((acc, { name, subItems }) => {
    return subItems.reduce((acc, subItem) => {
      return Object.assign({}, acc, { [subItem]: name });
    }, acc);
  }, {});
}

function getFieldMenuItems({
  subItemAction,
  productId,
  exchange,
  maturity,
  currentFieldSelector,
}: {
  subItemAction: (subItem: string) => void;
  productId: string;
  exchange: TSelectorExchange;
  maturity: TSelectorMaturity<TSelectorExchange>;
  currentFieldSelector?: string | null;
}): MenuConfig[] {
  const selectorsIntoSubgroups = selectorSubgroup();
  const selectors = getSelectors({ exchange, maturity, productId });

  const prioritySet = new Set(promotedSelectors);

  const priorityMenuItems = promotedSelectors
    .map((value) => selectors.find((s) => s.value === value))
    .filter(Boolean)
    .map((selector) => {
      const isSelected = currentFieldSelector === selector?.value;
      return {
        id: `field-${selector?.value}`,
        label: selector?.label,
        isSelected,
        action: () => subItemAction(selector?.value),
      } as MenuConfig;
    });

  const groups = unique(Object.values(selectorsIntoSubgroups));

  const groupedMenuItems = groups
    .map((group) => ({
      id: `field-group-${group}`,
      label: group,
      action: () => {},
      subMenu:
        (
          selectors
            .map((selector) => {
              const groupName = selectorsIntoSubgroups[selector.value];

              if (groupName === group && !prioritySet.has(selector.value)) {
                const isSelected = currentFieldSelector === selector.value;
                return {
                  id: `field-${selector.value}`,
                  label: selector.label,
                  isSelected,
                  action: () => subItemAction(selector.value),
                } as MenuConfig;
              }
            })
            .filter(Boolean) as MenuConfig[]
        ).sort((a, b) => {
          if (a === "separator" || b === "separator") return 0;
          return a.label.toString().localeCompare(b.label.toString());
        }) || [],
    }))
    .filter((group) => group.subMenu.length > 0) as MenuConfig[];

  const combined: MenuConfig[] = [];

  if (priorityMenuItems.length > 0) {
    combined.push(...priorityMenuItems);
  }

  if (priorityMenuItems.length > 0 && groupedMenuItems.length > 0) {
    combined.push("separator");
  }

  if (groupedMenuItems.length > 0) {
    combined.push(...groupedMenuItems);
  }

  return combined.length
    ? combined
    : [
        {
          id: "field-value-group",
          label: "VALUE",
          action: () => {},
          subMenu: [
            {
              id: "field-value",
              label: "VALUE",
              isSelected: currentFieldSelector === "value",
              action: () => subItemAction("value"),
            },
          ],
        },
      ];
}

export const fieldSection: MenuConfig[] = [
  {
    id: "field",
    label: "Field",
    icon: <InputPipeIcon />,
    disabled: any(
      selection.isMultiColumn,
      anyColumn(column.isAuxiliary),
      anyColumn(column.isBlank),
      anyColumn(column.isEod),
      anyColumn(column.hasFormula),
      anyColumn(column.isManualEntry),
    ),
    tooltip: (ctx) => {
      if (recipes.productOnly(ctx)) {
        return "Only available for product columns";
      }
      const selectorsList = getSelectors({
        exchange: ctx.exchange,
        maturity: ctx.maturity,
        productId: ctx.productId,
      });
      if ((selectorsList?.length || 0) <= 1) {
        return "No field options available for this product";
      }
      return undefined;
    },
    action: () => {},
    subMenu: (ctx) => {
      const product = ctx.pageProductsData?.find((x) => x.id === ctx.entityId);

      const fieldMenuItems = getFieldMenuItems({
        subItemAction: (subItem) => {
          if (ctx.columnId && product) {
            ctx.updatePageProducts.optimistic({
              id: product.id,
              column_field_selector: subItem,
            });

            if (anyColumn(column.isCannedOrSourced)(ctx)) {
              captureEvent("changed_field_value", {
                columnId: ctx.columnId,
                productId: ctx.productId,
              });
            }
          }
        },
        exchange: ctx.exchange,
        maturity: ctx.maturity,
        productId: ctx.productId,
        currentFieldSelector: product?.column_field_selector,
      });

      return fieldMenuItems;
    },
  },
];
